import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { useAuth } from '@/features/auth/session/auth-context'
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher'
import { ThemeSwitcher } from '@/shared/components/ThemeSwitcher'
import { elevaLinks } from '@/shared/config/links'

/**
 * Sections of the landing page the nav scrolls to (absolute, so they also
 * work from the marketing pages), then the marketing pages themselves.
 * Kept in display order.
 */
const NAV_ITEMS = [
  { key: 'explore', to: '/welcome#explore' },
  { key: 'map', to: '/welcome#map' },
  { key: 'communities', to: '/welcome#communities' },
  { key: 'creators', to: '/welcome#creators' },
  { key: 'business', to: '/welcome#business' },
  { key: 'about', to: elevaLinks.about },
  { key: 'contact', to: elevaLinks.contact },
] as const

/**
 * Header of the public landing page: logo, section links, theme, language,
 * sign in. The bar follows the visitor's theme like the page itself.
 */
export function LandingNav() {
  const { t } = useTranslation(['about', 'common'])
  const { isAuthenticated } = useAuth()

  return (
    <header className="bg-canvas/70 border-border sticky top-0 z-40 overflow-x-clip border-b backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Link to="/welcome" className="flex shrink-0 items-center gap-2.5">
          <img
            src="/assets/img/logo.png"
            alt=""
            width={36}
            height={36}
            draggable={false}
            className="size-9 rounded-full"
          />
          <span className="text-fg text-lg font-black tracking-[0.22em] uppercase">
            {t('common:app.name')}
          </span>
        </Link>

        <nav className="hidden min-w-0 items-center gap-0.5 xl:flex">
          {NAV_ITEMS.map(({ key, to }) => (
            <Link
              key={key}
              to={to}
              className="text-fg-muted hover:bg-fg/10 hover:text-fg rounded-full px-3 py-1.5 text-sm font-semibold whitespace-nowrap transition"
            >
              {t(`about:nav.${key}`)}
            </Link>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <ThemeSwitcher />
          <LanguageSwitcher />
          {/* Hidden on phones - the hero right below carries the same CTA. */}
          <Link
            to={isAuthenticated ? '/' : '/login'}
            className="bg-primary hover:bg-primary-hover hidden rounded-full px-4 py-1.5 text-sm font-bold whitespace-nowrap text-white transition hover:brightness-110 sm:inline-flex"
          >
            {isAuthenticated ? t('about:hero.ctaOpen') : t('common:nav.signIn')}
          </Link>
        </div>
      </div>
    </header>
  )
}
